import { useEffect, useMemo, useRef, useState } from 'react';
import { hasBlocks } from '../labEngine';
import { computeMDD, MOULDS } from './mddCalc';
import { mddContainerG, mouldG } from '../labMaster';
import './MDDTest.css';

const n = (s) => (s === '' || s == null ? NaN : Number(s));
const blankTrial = () => ({ mouldSoilG: '', contNo: '', wetG: '', dryG: '', tareG: '' });
const KEYS = ['mouldSoilG', 'contNo', 'wetG', 'dryG', 'tareG'];
// container weight: typed value wins, otherwise looked up from the container number (labMaster.js)
const tareOf = (t) => (t.tareG !== '' ? n(t.tareG) : (mddContainerG(t.contNo) ?? NaN));
const done = (t) => ['mouldSoilG', 'wetG', 'dryG'].every((k) => Number.isFinite(n(t[k]))) && Number.isFinite(tareOf(t));
const started = (t) => KEYS.some((k) => t[k] !== '');
const toTrial = (t) => ({ mouldSoilG: n(t.mouldSoilG), wetG: n(t.wetG), dryG: n(t.dryG), tareG: tareOf(t), contNo: t.contNo === '' ? null : Number(t.contNo) });

// Compaction (MDD / OMC) — IS 2720 (Part 8):1983, heavy compaction. Project details live in the portal.
export default function MDDTest({ others, onReport }) {
  const [setup, setSetup] = useState({ volume: '2250', mouldNo: '', mouldMass: '', retained19: '', procedure: '', gs: '' });
  const [trials, setTrials] = useState(() => Array.from({ length: 6 }, blankTrial));
  const setS = (k) => (e) => setSetup((s) => ({ ...s, [k]: e.target.value }));
  const setT = (i, k) => (e) => setTrials((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const mould = MOULDS.find((m) => String(m.volumeCc) === setup.volume);
  const mouldFromNo = mouldG(setup.mouldNo);                       // weight looked up from the mould number
  const mouldMassEff = setup.mouldMass !== '' ? n(setup.mouldMass) : (mouldFromNo ?? NaN);
  const gsaRet19 = (() => { // % retained on 19 mm = 100 − % passing 19 mm (from the GSA test, if filled)
    const p = others?.gsa?.payload?.result?.rows?.find((r) => r.size === 19)?.pctPassing;
    return Number.isFinite(p) ? 100 - p : null;
  })();

  const ret19Eff = setup.retained19 !== '' ? n(setup.retained19) : (gsaRet19 ?? NaN); // typed value wins, else from the GSA test
  const good = trials.filter(done);
  const setupMissing = [['mould weight (or a listed mould no.)', mouldMassEff], ['% retained on 19 mm (or fill the GSA test)', ret19Eff]]
    .filter(([, v]) => !Number.isFinite(n(v))).map(([l]) => l).concat(setup.procedure ? [] : ['procedure']);
  const canCompute = setupMissing.length === 0 && good.length > 0;

  const inputs = useMemo(() => canCompute ? {
    mouldVolumeCc: Number(setup.volume), mouldNo: setup.mouldNo === '' ? null : Number(setup.mouldNo), mouldMassG: mouldMassEff, procedure: setup.procedure,
    retained19Pct: ret19Eff, gs: setup.gs === '' ? null : n(setup.gs), trials: good.map(toTrial),
  } : null, [canCompute, setup, trials, mouldMassEff, ret19Eff]); // eslint-disable-line react-hooks/exhaustive-deps

  const { result, issues } = useMemo(() => (inputs ? computeMDD(inputs) : { result: null, issues: [] }), [inputs]);
  const ready = !!result && !hasBlocks(issues);
  const dirty = trials.some(started) || ['mouldNo', 'mouldMass', 'retained19', 'procedure', 'gs'].some((k) => setup[k] !== '');
  const hint = ready ? '' : !dirty ? 'enter the readings'
    : setupMissing.length ? `fill ${setupMissing[0]}${setupMissing.length > 1 ? ` (+${setupMissing.length - 1} more)` : ''}`
      : !good.length ? 'complete a trial' : (issues.find((i) => i.level === 'block')?.msg || '');

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty', ready, dirty, hint,
    summary: ready ? `MDD ${result.mdd.toFixed(2)} g/cc · OMC ${result.omc.toFixed(1)} %` : '',
    payload: ready ? { inputs, result, issues } : null,
  }), [ready, dirty, hint, result, issues, inputs]);
  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  // live dry density per trial
  const rowCalc = (t) => {
    if (!done(t) || !(mouldMassEff > 0) || !mould) return null;
    const x = toTrial(t);
    if (!(x.mouldSoilG > mouldMassEff) || !(x.dryG > x.tareG) || !(x.wetG >= x.dryG)) return null;
    const bulk = (x.mouldSoilG - mouldMassEff) / mould.volumeCc;
    const w = ((x.wetG - x.dryG) / (x.dryG - x.tareG)) * 100;
    return { w, dry: (100 * bulk) / (100 + w), soil: x.mouldSoilG - mouldMassEff, bulk, water: x.wetG - x.dryG, dryMass: x.dryG - x.tareG };
  };
  const num = (value, onChange, label, step = '0.01') => (
    <input className="mdd-input" type="number" step={step} min="0" inputMode="decimal" aria-label={label} value={value} onChange={onChange} />
  );

  return (
    <div className="mdd">
      <section>
        <div className="mdd-sec-head"><h4>Mould &amp; procedure</h4><span className="mdd-muted">heavy compaction · 4.9 kg rammer · 5 layers</span></div>
        <div className="mdd-three">
          <label className="mdd-field">Mould *
            <select className="mdd-input" value={setup.volume} onChange={setS('volume')}>
              {MOULDS.map((m) => <option key={m.volumeCc} value={m.volumeCc}>{m.label}</option>)}
            </select>
            <span className="mdd-muted">{mould ? `${mould.blows} blows per layer` : ''}</span></label>
          <label className="mdd-field">Mould no. <span className="mdd-opt">optional</span>{num(setup.mouldNo, setS('mouldNo'), 'Mould no.', '1')}
            <span className="mdd-muted">{setup.mouldNo === '' ? 'fills the weight automatically' : mouldFromNo != null ? `weight ${mouldFromNo} g from the mould list` : 'not in the mould list — type the weight'}</span></label>
          <label className="mdd-field">Mould weight (g) *
            <input className="mdd-input" type="number" step="1" min="0" inputMode="decimal" aria-label="Mould weight" value={setup.mouldMass}
              placeholder={mouldFromNo != null ? String(mouldFromNo) : ''} onChange={setS('mouldMass')} /></label>
          <label className="mdd-field">Procedure *
            <select className="mdd-input" value={setup.procedure} onChange={setS('procedure')}>
              <option value="">—</option><option value="single">Single sample</option><option value="separate">Separate samples</option>
            </select></label>
          <label className="mdd-field">% retained on 19 mm *<input className="mdd-input" type="number" step="0.1" min="0" inputMode="decimal" aria-label="Retained on 19 mm" value={setup.retained19}
              placeholder={gsaRet19 != null ? gsaRet19.toFixed(1) : ''} onChange={setS('retained19')} />
            <span className="mdd-muted">{setup.retained19 !== '' ? 'typed value used' : gsaRet19 != null ? 'taken from the GSA test' : 'fill the GSA test or type it'}</span></label>
          <label className="mdd-field">Specific gravity <span className="mdd-opt">optional</span>{num(setup.gs, setS('gs'), 'Specific gravity', '0.01')}
            <span className="mdd-muted">draws the zero-air-voids line</span></label>
        </div>
      </section>

      <section>
        <div className="mdd-sec-head"><h4>Trials</h4><span className="mdd-muted">minimum 5, with the peak in the middle</span></div>
        <div className="mdd-scroll">
          <div className="mdd-grid mdd-labels">
            <span>#</span><span>Mould + soil (g)</span><span>Container no.</span><span>Container + wet (g)</span><span>Container + dry (g)</span><span>Container (g)</span><span>w % · dry density</span>
          </div>
          {trials.map((t, i) => {
            const r = rowCalc(t);
            return (
              <div key={i} className="mdd-grid mdd-det">
                <span className="mdd-no">{i + 1}</span>
                {KEYS.map((k, c) => (
                  <input key={k} className="mdd-input" type="number" step={c === 0 || k === 'contNo' ? '1' : '0.01'} min="0" inputMode="decimal"
                    aria-label={`Trial ${i + 1} ${k}`} value={t[k]} onChange={setT(i, k)}
                    placeholder={k === 'tareG' && t.contNo !== '' && mddContainerG(t.contNo) != null ? String(mddContainerG(t.contNo)) : ''}
                    title={k === 'contNo' && t.contNo !== '' && mddContainerG(t.contNo) == null ? 'Container not in the list — type its weight' : undefined} />))}
                <span className="mdd-calc">{r ? <>{r.dry.toFixed(3)}<small>w {r.w.toFixed(1)}%</small></> : '—'}</span>
                {trials.length > 5 && <button type="button" className="mdd-x" aria-label={`Remove trial ${i + 1}`}
                  onClick={() => setTrials((rows) => rows.filter((_, j) => j !== i))}>×</button>}
              </div>);
          })}
        </div>
        <button type="button" className="mdd-link" onClick={() => setTrials((r) => [...r, blankTrial()])}>+ Add trial</button>
      </section>

      <section>
        <div className="mdd-sec-head"><h4>Calculated values</h4><span className="mdd-muted">same rows as the Excel sheet — nothing to type here</span></div>
        <div className="mdd-scroll">
          <table className="mdd-table">
            <thead><tr><th>Trial</th>{trials.map((_, i) => <th key={i}>{i + 1}</th>)}</tr></thead>
            <tbody>
              {[
                ['Wt. of wet soil w3 = w2 − w1 (g)', (r) => r.soil, 0],
                ['Wet density = w3 / V (g/cc)', (r) => r.bulk, 3],
                ['Wt. of water w8 = w6 − w7 (g)', (r) => r.water, 2],
                ['Wt. of dry soil w9 = w7 − w5 (g)', (r) => r.dryMass, 2],
                ['Water content = 100 · w8 / w9 (%)', (r) => r.w, 2],
                ['Dry density (g/cc)', (r) => r.dry, 3, true],
              ].map(([label, fn, nd, bold]) => (
                <tr key={label} className={bold ? 'is-bold' : ''}><td>{label}</td>
                  {trials.map((t, i) => { const r = rowCalc(t); return <td key={i}>{r ? fn(r).toFixed(nd) : '—'}</td>; })}</tr>))}
            </tbody>
          </table>
        </div>
      </section>

      {result && (
        <section className="mdd-result">
          <div className="mdd-stat"><span>Max dry density</span><b>{result.mdd.toFixed(2)}</b><small>g/cc</small></div>
          <div className="mdd-stat"><span>Optimum moisture</span><b>{result.omc.toFixed(1)}</b><small>%</small></div>
          <div className="mdd-stat"><span>Retained on 19 mm</span><b>{result.retained19Pct ?? '—'}</b><small>%</small></div>
        </section>
      )}
      {issues.length > 0 && (
        <ul className="mdd-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'mdd-err' : 'mdd-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="mdd-muted">({it.clause})</span>
            </li>))}
        </ul>
      )}
      {dirty && setupMissing.length > 0 && <p className="mdd-missing"><b>Still to fill:</b> {setupMissing.join(', ')}</p>}
    </div>
  );
}
